import {InvalidArgumentException} from "./errors/Exceptions";

/**
 * Performs shallow (using <code>===</code>) element-by-element comparison of given arrays
 * @param arr1 {[]}
 * @param arr2 {[]}
 */
export const areArraysEqual = (arr1, arr2) => {
    if(!arr1 || !arr2){
        return false;
    }

    return arr1.length === arr2.length &&
        arr1.every((el, i) => el === arr2[i]);
}

/**
 * Downloads file with given <code>content</code> on user's computer
 *
 * Source: https://medium.com/@iyiolaosuagwu/download-a-string-as-a-txt-file-in-react-7d55efb22536
 *
 * @param filename {string} filename (with extension)
 * @param content
 * @param filetype {string} <code>text/plain</code> or <code>application/json</code>
 */
export const exportFile = (filename, content, filetype) => {
    const element = document.createElement("a");

    const file = new Blob([content], {
        type: filetype
    });
    element.href = URL.createObjectURL(file);
    element.download = filename;

    document.body.appendChild(element);
    element.click();
    element.remove(); // need or don't need ?????
}

/**
 *
 * @param filename {string}
 * @param content {string}
 */
export const exportTextFile = (filename, content) => {
    exportFile(filename, content, 'text/plain');
}

/**
 *
 * @param filename {string}
 * @param content {string}
 */
export const exportJsonFile = (filename, content) => {
    exportFile(filename, content, 'application/json');
}


/**
 * Checks if given <code>obj</code> is <code>string</code>.
 * @param obj
 * @return {boolean}
 */
export const isString = (obj) => {
    return (typeof obj === 'string' || obj instanceof String);
}

/**
 * Checks if given <code>str</code> contains any whitespace character.
 * @param str {string}
 * @return {boolean}
 */
export const containsWhitespace = (str) => {
    return isString(str) && /\s/.test(str)
}

/**
 * Checks if <code>obj</code> has method named <code>methodName</code> with
 * <code>argsNumber</code> number of arguments.
 * @param obj {any}
 * @param methodName {string}
 * @param argsNumber {number} [optional]
 */
export const hasMethod = (obj, methodName, argsNumber) => {
    return (typeof obj === 'object') && (obj !== null) &&
        (methodName in obj) &&
        (typeof obj[methodName] === 'function') &&
        (argsNumber === undefined || obj[methodName].length === argsNumber);
    // https://stackoverflow.com/a/41008152
}

/**
 * Compares given objects by <code>===</code> operator,
 * and if that is false -- compares by using <code>o1.equals(o2)</code>
 * (of course, if <code>o1</code> has appropriate method).
 *
 * @param o1 {any}
 * @param o2 {any}
 * @return {boolean}
 */
export const areEqualByOperatorOrMethod = (o1, o2) => {
    return o1 === o2 ||
        (hasMethod(o1, 'equals', 1) && o1.equals(o2));
}

/**
 * Checks if given array <code>arr</code> contains element that equals to given <code>item</code>.
 *
 * **WARNING:** comparison of elements is performed by <code>areEqualByOperatorOrMethod()</code> function.
 * @param arr {any[]}
 * @param item {any}
 * @return {boolean}
 */
export const containsEqualElement = (arr, item) => {
    if(!Array.isArray(arr)){
        throw new InvalidArgumentException("arr must be an Array");
    }

    let result = false;
    arr.forEach((i) => {
        if(areEqualByOperatorOrMethod(item, i)){
            result = true;
        }
    });
    return result;
}

/**
 * Checks if given array <code>arr1</code> contains each element of second array <code>arr2</code>.
 * If <code>arr2</code> is empty (or contains only empty elements), returns <code>true</code>.
 *
 * **WARNING:** this method uses <code>containsEqualElement()</code> function.
 * @param arr1 {any[]}
 * @param arr2 {any[]}
 * @return {boolean}
 */
export const containsArrayByMethod = (arr1, arr2) => {
    if(!Array.isArray(arr1)){
        throw new InvalidArgumentException("arr1 must be an Array");
    }
    if(!Array.isArray(arr2)){
        throw new InvalidArgumentException("arr2 must be an Array");
    }

    let result = true;
    arr2.forEach((item) => {
        if(!containsEqualElement(arr1, item)){
            result = false;
        }
    });
    return result;
}

/**
 * Returns swallow copy of given array <code>arr</code> with unique items and without empty cells.
 *
 * **WARNING:** comparison of elements is performed by <code>areEqualByOperatorOrMethod()</code> function.
 * @param arr {any}
 * @return {any[]}
 */
export const removeRepeatsAndEmptyItems = (arr) => {
    if(!Array.isArray(arr)){
        throw new InvalidArgumentException("arr must be an Array");
    }

    let resultArr = [];
    for(const item of arr){
        if(item === undefined){ // skip empty
            continue;
        }
        if(!containsEqualElement(resultArr, item)){ // skip repeats
            resultArr.push(item);
        }
    }

    return resultArr;
}

/**
 * Converts array to string
 *
 * **WARNING:** this method uses <code>.toString()</code> method of each element of <code>arr</code>.
 * @param arr {any[]} array to be converted to string
 * @param sep {string} separator that will be placed between elements
 * @param brackets {boolean} indicates whether put brackets (<code>[]</code>) into string, or not
 * @return {string}
 */
export const arrayToString = (arr, sep = " ", brackets = false) => {
    if(!Array.isArray(arr)){
        throw new InvalidArgumentException("arr must be an Array");
    }

    let str = "";

    if(brackets){
        str += "[";
    }
    for(const item of arr){
        str += item.toString() + sep;
    }
    if(sep.length > 0){
        str = str.slice(0, -1 * sep.length); // delete last separator
    }
    if(brackets){
        str += "]";
    }

    return str;
}

/**
 * Generates a random integer between the specified values.
 *
 * <a href="https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Math/random#getting_a_random_integer_between_two_values">Source</a>
 * @param min lower bound of interval (inclusively)
 * @param max upper bound of interval (exclusively)
 * @return {number}
 */
export const getRandomInt = (min, max) => {
    const minCeiled = Math.ceil(min);
    const maxFloored = Math.floor(max);
    return Math.floor(Math.random() * (maxFloored - minCeiled) + minCeiled);
}

/**
 * Inserts given <code>item</code> into <code>arr</code> at specified <code>index</code>.
 *
 * <a href="https://www.freecodecamp.org/news/insert-into-javascript-array-at-specific-index/">Source</a>
 *
 * @param arr {any[]}
 * @param index {number} must be in <code>[0, arr.length]</code> interval
 * @param item {any} item to insert at given index <code>index</code>
 * @return {any[]} swallow copy of given <code>arr</code> with <code>item</code> inserted at given <code>index</code>
 */
export const insertIntoArray = (arr, index, item) => {
    if(!Array.isArray(arr)){
        throw new InvalidArgumentException("arr must be an Array");
    }
    if(!Number.isInteger(index)){
        throw new InvalidArgumentException("index must be an integer Number");
    }
    if(index < 0 || index > arr.length){
        throw new InvalidArgumentException("index must be in interval [0, arr.length]");
    }

    return [
        ...arr.slice(0, index),
        item,
        ...arr.slice(index)
    ];
}


/**
 * Returns <code>str</code>, trimmed by <code>trimmedChar</code>
 * @param str {string}
 * @param trimmedChar {string}
 * @return {string}
 */
export const trimStringByChar = (str, trimmedChar) => {
    let leftmostNonTrimmedCharIndex = undefined;
    let rightmostNonTrimmedCharIndex = undefined;

    // find leftmost
    let idx = 0;
    while(idx < str.length && str.charAt(idx) === trimmedChar){
        idx++;
    }
    if(idx === str.length){
        return ""; // no non-trimmedChar in str
    }
    leftmostNonTrimmedCharIndex = idx;

    // find rightmost
    idx = str.length - 1;
    while(idx > -1 && str.charAt(idx) === trimmedChar){
        idx--;
    } // no need to check if idx is -1, because at least we have leftmost non-trimmedChar char
    rightmostNonTrimmedCharIndex = idx;

    return str
        .substring(leftmostNonTrimmedCharIndex, rightmostNonTrimmedCharIndex + 1);
}

/**
 * @param str {string}
 * @param ifEmpty {number}
 * @returns {number} number parsed from given string, or <code>ifEmpty</code> if trimmed <code>str</code> is empty
 */
export const convertStrToInt = (str, ifEmpty) => {
    if(str.trim().length === 0) {
        return ifEmpty;
    }
    return parseInt(str.trim());
}